import { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Dropdown,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd'
import { AuditOutlined, CheckOutlined, CloseOutlined, MoreOutlined, RedoOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { getQuoteTag } from '@/components/scheda-v2/FaseAnalisi'
import {
  useRichiesteOfficine,
  useApprovaQuoteRichieste,
  useRespingiQuoteRichieste,
  useRinegoziaQuoteRichieste,
} from '@/hooks/useWorkshopQuotes'
import { usePermission } from '@/hooks/usePermission'
import type { WorkshopQuoteRichiesta } from '@/types/workshopQuotes'

const { Text, Title } = Typography

type ActionKind = 'respingi' | 'rinegozia'

function formatOfferta(q: WorkshopQuoteRichiesta): string | null {
  if (q.importo == null) return null
  const imp = `€ ${Number(q.importo).toFixed(2).replace('.', ',')}`
  const parti: string[] = [imp]
  if (q.aliquota_iva != null) parti.push(`+ IVA ${q.aliquota_iva}%`)
  if (q.giorni_lavorazione != null) parti.push(`· ${q.giorni_lavorazione}gg`)
  parti.push(q.ricambi_inclusi ? '· con ricambi' : '· solo MO')
  return parti.join(' ')
}

export default function PreventiviOfficinePage() {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const canApprove = usePermission('workshop_quotes.approve')

  const [soloAperte, setSoloAperte] = useState(true)
  const [actionModal, setActionModal] = useState<{
    open: boolean
    kind: ActionKind
    quoteId: number
  } | null>(null)
  const [motivo, setMotivo] = useState('')

  const { data, isLoading, error } = useRichiesteOfficine(soloAperte)
  const approva = useApprovaQuoteRichieste()
  const respingi = useRespingiQuoteRichieste()
  const rinegozia = useRinegoziaQuoteRichieste()

  const openModal = (kind: ActionKind, quoteId: number) => {
    setMotivo('')
    setActionModal({ open: true, kind, quoteId })
  }

  const handleModalOk = () => {
    if (!actionModal) return
    if (!motivo.trim()) return
    const body = { motivo: motivo.trim() }
    if (actionModal.kind === 'respingi') {
      respingi.mutate({ quoteId: actionModal.quoteId, body }, { onSuccess: () => setActionModal(null) })
    } else {
      rinegozia.mutate({ quoteId: actionModal.quoteId, body }, { onSuccess: () => setActionModal(null) })
    }
  }

  const columns: ColumnsType<WorkshopQuoteRichiesta> = [
    {
      title: 'Scheda',
      dataIndex: 'work_order_numero',
      width: 100,
      render: (val, r) => (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontWeight: 600 }}
            onClick={() => navigate(`/schede-lavoro/${r.work_order_id}`)}
          >
            {val || `#${r.work_order_id}`}
          </Button>
      ),
    },
    {
      title: 'Targa',
      dataIndex: 'veicolo_targa',
      width: 90,
      render: (val) => val ? <Tag style={{ fontFamily: 'monospace', margin: 0 }}>{val}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Intervento',
      dataIndex: 'intervento_descrizione',
      ellipsis: true,
      render: (val) => <Text>{val ?? '—'}</Text>,
    },
    {
      title: 'Stato',
      dataIndex: 'stato',
      width: 110,
      render: (_, r) => {
        const { label, color } = getQuoteTag(r)
        return <Tag color={color}>{label}</Tag>
      },
    },
    {
      title: 'Offerta',
      width: 220,
      render: (_, r) => {
        const offerta = formatOfferta(r)
        if (!offerta) return <Text type="secondary" style={{ fontSize: 12 }}>In attesa</Text>
        const nota = r.note_officina
        return (
          <div>
            <Text style={{ fontSize: 12 }}>{offerta}</Text>
            {nota && (
              <div style={{ fontSize: 11, color: token.colorTextSecondary, marginTop: 2 }}>
                {nota}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: 'Scadenza',
      dataIndex: 'data_scadenza_risposta',
      width: 100,
      render: (val) => {
        if (!val) return <Text type="secondary">—</Text>
        const scaduta = dayjs(val).isBefore(dayjs(), 'day')
        return (
          <Text style={{ fontSize: 12, color: scaduta ? token.colorError : undefined }}>
            {dayjs(val).format('DD/MM/YY')}
          </Text>
        )
      },
    },
    ...(canApprove
      ? ([
          {
            title: 'Azioni',
            width: 120,
            render: (_: unknown, r: WorkshopQuoteRichiesta) => {
              if (r.stato !== 'risposta_ricevuta') return null
              const menuItems = [
                {
                  key: 'rinegozia',
                  icon: <RedoOutlined />,
                  label: 'Rinegozia',
                  onClick: () => openModal('rinegozia', r.id),
                },
                {
                  key: 'respingi',
                  icon: <CloseOutlined />,
                  label: 'Respingi',
                  danger: true,
                  onClick: () => openModal('respingi', r.id),
                },
              ]
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <Space size={4}>
                    {r.accettato_da_officina && (
                      <Popconfirm
                        title="Approvare questo preventivo?"
                        onConfirm={() => approva.mutate({ quoteId: r.id, body: {} })}
                        okText="Approva"
                        cancelText="Annulla"
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={approva.isPending}
                        >
                          Approva
                        </Button>
                      </Popconfirm>
                    )}
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                      <Button type="default" icon={<MoreOutlined />} size="small" />
                    </Dropdown>
                  </Space>
                </div>
              )
            },
          },
        ] as ColumnsType<WorkshopQuoteRichiesta>)
      : []),
  ]

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space align="center">
          <AuditOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0 }}>
            Preventivi officine
          </Title>
          {data && data.totale_da_gestire > 0 && (
            <Badge count={data.totale_da_gestire} color={token.colorWarning} />
          )}
        </Space>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>Solo aperte</Text>
          <Switch checked={soloAperte} onChange={setSoloAperte} size="small" />
        </Space>
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )}

      {error && (
        <Alert type="error" message="Impossibile caricare i preventivi" style={{ marginBottom: 16 }} />
      )}

      {data && data.officine.length === 0 && (
        <Alert type="info" message="Nessun preventivo trovato" />
      )}

      {data && data.officine.length > 0 && (
        <Collapse
          defaultActiveKey={data.officine.map((g) => String(g.workshop_id))}
          style={{ background: 'transparent' }}
        >
          {data.officine.map((group) => (
            <Collapse.Panel
              key={String(group.workshop_id)}
              header={
                <Space>
                  <Text strong>{group.workshop_nome ?? `Officina #${group.workshop_id}`}</Text>
                  {group.da_gestire > 0 && (
                    <Badge count={group.da_gestire} color={token.colorWarning} />
                  )}
                  {group.scadute > 0 && (
                    <Tag color="red" style={{ margin: 0 }}>
                      {group.scadute} scadut{group.scadute === 1 ? 'a' : 'e'}
                    </Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {group.totale} totale
                  </Text>
                </Space>
              }
              style={{
                marginBottom: 8,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
              }}
            >
              <Table<WorkshopQuoteRichiesta>
                dataSource={group.quotes}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={false}
              />
            </Collapse.Panel>
          ))}
        </Collapse>
      )}

      <Modal
        open={actionModal?.open ?? false}
        title={actionModal?.kind === 'respingi' ? 'Respingi preventivo' : 'Rinegozia preventivo'}
        onCancel={() => setActionModal(null)}
        onOk={handleModalOk}
        okText={actionModal?.kind === 'respingi' ? 'Respingi' : 'Rinegozia'}
        okButtonProps={{
          danger: actionModal?.kind === 'respingi',
          loading: respingi.isPending || rinegozia.isPending,
          disabled: !motivo.trim(),
        }}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {actionModal?.kind === 'respingi'
              ? 'Indica il motivo del rifiuto (visibile nel log interno).'
              : 'Descrivi cosa chiedere di rivedere. Il testo sarà inviato all\'officina.'}
          </Text>
        </div>
        <Input.TextArea
          rows={4}
          placeholder="Motivo…"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
        />
      </Modal>
    </div>
  )
}
